import type { HouseholdEvent, ProgressReport } from "@tutor/protocol";
import { Icon, type IconName } from "@tutor/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import * as authApi from "../auth/api";
import { loadDevice, type DeviceRecord } from "../auth/storage";
import { Alerts } from "./Alerts";
import * as api from "./api";
import { Consent } from "./Consent";
import { EvalReview } from "./EvalReview";
import { errorKey, needsFreshSignIn } from "./errors";
import { Goals } from "./Goals";
import { useHouseholdLive, type SocketFactory } from "./live";
import { PauseBanner } from "./PauseBanner";
import { Remember, RememberedDevices, Unlock } from "./ParentPin";
import { ProgressTab } from "./ProgressTab";
import { SignIn } from "./SignIn";
import { useParentT, type ParentStringKey } from "./strings";
import "@tutor/ui/parent.css";
import "./parent.css";

// Parent mode on the child's device: the parent PIN where this device remembers a parent
// (ADR-0048), otherwise an email-code sign-in, then one child's
// progress, goals, consent and alerts, kept current by the household live channel.
// Leaving (button or 10 idle minutes) returns to the lesson underneath, which stays
// mounted and connected the whole time.

/** Idle time after which parent mode closes itself. */
export const PARENT_IDLE_MS = 10 * 60 * 1000;

type Tab = "progress" | "goals" | "consent" | "alerts";
const TABS: readonly Tab[] = ["progress", "goals", "consent", "alerts"];
const TAB_ICONS: Readonly<Record<Tab, IconName>> = {
  progress: "peak",
  goals: "settings",
  consent: "check",
  alerts: "alert",
};

type Auth =
  | { readonly kind: "checking" }
  | { readonly kind: "pin"; readonly grants: readonly api.DeviceGrant[] }
  | { readonly kind: "signin"; readonly expired: boolean; readonly notice?: ParentStringKey }
  | { readonly kind: "remember" }
  | { readonly kind: "ready" };

interface Toast {
  readonly paused: boolean;
  readonly id: number;
}

export function ParentMode(props: { onLeave: () => void; createSocket?: SocketFactory }) {
  const { onLeave } = props;
  const t = useParentT();
  const [auth, setAuth] = useState<Auth>({ kind: "checking" });
  const [device, setDevice] = useState<DeviceRecord>();
  const [profiles, setProfiles] = useState<readonly authApi.ParentProfile[] | null>(null);
  const [profileId, setProfileId] = useState<string>();
  const [tab, setTab] = useState<Tab>("progress");
  const [report, setReport] = useState<ProgressReport | null>(null);
  const [alerts, setAlerts] = useState<readonly api.ParentAlertRow[]>([]);
  const [error, setError] = useState<ParentStringKey>();
  const [goalsRefresh, setGoalsRefresh] = useState(0);
  const [consentRefresh, setConsentRefresh] = useState(0);
  const [toast, setToast] = useState<Toast | null>(null);

  const onAuthError = useCallback((e: api.ApiError) => {
    if (!needsFreshSignIn(e)) return false;
    setAuth({ kind: "signin", expired: true });
    return true;
  }, []);

  // Close after PARENT_IDLE_MS without a tap or key press.
  const leave = useRef(onLeave);
  leave.current = onLeave;
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let timer = setTimeout(() => {
      leave.current();
    }, PARENT_IDLE_MS);
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        leave.current();
      }, PARENT_IDLE_MS);
    };
    const node = root.current;
    node?.addEventListener("pointerdown", reset);
    node?.addEventListener("keydown", reset);
    return () => {
      clearTimeout(timer);
      node?.removeEventListener("pointerdown", reset);
      node?.removeEventListener("keydown", reset);
    };
  }, []);

  // A device that remembers a parent opens with the parent PIN.
  useEffect(() => {
    const run = { live: true };
    const alive = () => run.live;
    void (async () => {
      const stored = await loadDevice().catch(() => undefined);
      const record = stored?.device;
      if (!alive()) return;
      setDevice(record);
      if (record === undefined) {
        setAuth({ kind: "signin", expired: false });
        return;
      }
      const res = await api.getDeviceGrants(record.credential);
      if (!alive()) return;
      setAuth(
        res.ok && res.value.grants.length > 0
          ? { kind: "pin", grants: res.value.grants }
          : { kind: "signin", expired: false },
      );
    })();
    return () => {
      run.live = false;
    };
  }, []);

  // After an email code on a paired device, offer to stay signed in here.
  const signedIn = useCallback(async () => {
    if (device === undefined) {
      setAuth({ kind: "ready" });
      return;
    }
    const res = await api.listGrants();
    const known = res.ok && res.value.grants.some((g) => g.deviceId === device.deviceId);
    setAuth(res.ok && !known ? { kind: "remember" } : { kind: "ready" });
  }, [device]);

  const loadProfiles = useCallback(async () => {
    const res = await authApi.getMe();
    if (!res.ok) {
      if (!onAuthError(res.error)) setError(errorKey(res.error));
      return;
    }
    setError(undefined);
    setProfiles(res.value.profiles);
    const only = res.value.profiles.length === 1 ? res.value.profiles[0] : undefined;
    if (only !== undefined) setProfileId(only.id);
  }, [onAuthError]);

  const loadAlerts = useCallback(async () => {
    const res = await api.getAlerts();
    if (res.ok) setAlerts(res.value.alerts);
    else onAuthError(res.error);
  }, [onAuthError]);

  const loadReport = useCallback(
    async (id: string) => {
      setReport(null);
      const res = await api.getReport(id, 7);
      if (res.ok) {
        setReport(res.value);
        setError(undefined);
      } else if (!onAuthError(res.error)) setError(errorKey(res.error));
    },
    [onAuthError],
  );

  const ready = auth.kind === "ready";
  useEffect(() => {
    if (ready && profiles === null) void loadProfiles();
  }, [ready, profiles, loadProfiles]);

  useEffect(() => {
    if (!ready || profileId === undefined) return;
    void loadReport(profileId);
    void loadAlerts();
  }, [ready, profileId, loadReport, loadAlerts]);

  const onEvent = useCallback(
    (event: HouseholdEvent) => {
      switch (event.type) {
        case "parent.alert":
          setToast({ paused: event.pauseSession, id: Date.now() });
          void loadAlerts();
          break;
        case "goals.updated":
          if (event.profileId === profileId) setGoalsRefresh((n) => n + 1);
          break;
        case "consent.updated":
          if (event.profileId === profileId) setConsentRefresh((n) => n + 1);
          break;
      }
    },
    [profileId, loadAlerts],
  );
  useHouseholdLive(ready, onEvent, props.createSocket);

  const childAlerts = alerts.filter((a) => a.profileId === profileId);
  const unread = childAlerts.filter((a) => a.readAt === null).length;

  let content;
  if (auth.kind === "checking") {
    content = <p className="parent-muted">{t("parent.loading")}</p>;
  } else if (auth.kind === "pin" && device !== undefined) {
    content = (
      <Unlock
        device={device.credential}
        grants={auth.grants}
        onUnlocked={() => {
          setAuth({ kind: "ready" });
        }}
        onUseEmail={(notice) => {
          setAuth({ kind: "signin", expired: false, ...(notice ? { notice } : {}) });
        }}
      />
    );
  } else if (auth.kind === "signin" || auth.kind === "pin") {
    const expired = auth.kind === "signin" && auth.expired;
    const notice = auth.kind === "signin" ? auth.notice : undefined;
    content = (
      <SignIn
        // A new form (empty code) for each sign-in request.
        key={expired ? "expired" : (notice ?? "first")}
        expired={expired}
        {...(notice === undefined ? {} : { notice })}
        onSignedIn={() => void signedIn()}
      />
    );
  } else if (auth.kind === "remember" && device !== undefined) {
    content = (
      <Remember
        device={device.credential}
        onAuthError={onAuthError}
        onDone={() => {
          setAuth({ kind: "ready" });
        }}
      />
    );
  } else if (profiles === null || profileId === undefined) {
    content =
      profiles === null ? (
        error === undefined ? (
          <p className="parent-muted">{t("parent.loading")}</p>
        ) : (
          <div className="parent-section">
            <p className="parent-message" role="alert">
              {t(error)}
            </p>
            <button type="button" className="parent-button" onClick={() => void loadProfiles()}>
              {t("parent.retry")}
            </button>
          </div>
        )
      ) : (
        <section className="parent-section" aria-labelledby="parent-picker-title">
          <h2 id="parent-picker-title" className="parent-heading">
            {t("picker.title")}
          </h2>
          {profiles.length === 0 ? <p className="parent-muted">{t("picker.empty")}</p> : null}
          <ul className="parent-picker">
            {profiles.map((p) => (
              <li key={p.id}>
                {/* Display names appear only on this picker. */}
                <button
                  type="button"
                  className="parent-button parent-child"
                  lang={p.locale}
                  onClick={() => {
                    setProfileId(p.id);
                    setTab("progress");
                  }}
                >
                  {p.displayName}
                </button>
              </li>
            ))}
          </ul>
        </section>
      );
  } else {
    content = (
      <>
        <PauseBanner profileId={profileId} alerts={childAlerts} onAuthError={onAuthError} />
        <div className="parent-tabs" role="tablist">
          {TABS.map((name) => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={tab === name}
              className="parent-tab"
              aria-label={
                name === "alerts" && unread > 0
                  ? t("tab.alertsUnread", { count: unread })
                  : undefined
              }
              onClick={() => {
                setTab(name);
              }}
            >
              <Icon name={TAB_ICONS[name]} size={18} className="parent-tab__icon" />
              <span className="parent-tab__label">{t(`tab.${name}`)}</span>
              {name === "alerts" && unread > 0 ? (
                <span className="parent-tab__count" aria-hidden="true">
                  {unread}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        <div className="parent-tabpanel" role="tabpanel">
          {tab === "progress" ? (
            report === null ? (
              error === undefined ? (
                <p className="parent-muted">{t("parent.loading")}</p>
              ) : (
                <p className="parent-message" role="alert">
                  {t(error)}
                </p>
              )
            ) : (
              <ProgressTab report={report} alerts={childAlerts} onAuthError={onAuthError} />
            )
          ) : null}
          {tab === "goals" ? (
            <Goals profileId={profileId} refresh={goalsRefresh} onAuthError={onAuthError} />
          ) : null}
          {tab === "consent" ? (
            <>
              <Consent profileId={profileId} refresh={consentRefresh} onAuthError={onAuthError} />
              <EvalReview onAuthError={onAuthError} />
              <RememberedDevices
                thisDeviceId={device?.deviceId}
                onAuthError={onAuthError}
                onSignedOut={onLeave}
              />
            </>
          ) : null}
          {tab === "alerts" ? (
            <Alerts
              alerts={childAlerts}
              onAuthError={onAuthError}
              onRead={(id) => {
                setAlerts((list) =>
                  list.map((a) =>
                    a.id === id ? { ...a, readAt: a.readAt ?? new Date().toISOString() } : a,
                  ),
                );
              }}
            />
          ) : null}
        </div>
        {profiles.length > 1 ? (
          <button
            type="button"
            className="parent-link"
            onClick={() => {
              setProfileId(undefined);
              setReport(null);
            }}
          >
            {t("picker.switch")}
          </button>
        ) : null}
      </>
    );
  }

  return (
    <div
      className="parent-mode"
      ref={root}
      role="dialog"
      aria-modal="true"
      aria-label={t("parent.title")}
    >
      <header className="parent-header">
        <div className="parent-titles">
          <p className="parent-brand">{t("parent.brand")}</p>
          <h1 className="parent-title">{t("parent.title")}</h1>
        </div>
        <button type="button" className="parent-button parent-leave" onClick={onLeave}>
          <Icon name="close" size={18} />
          <span>{t("parent.leave")}</span>
        </button>
      </header>
      {toast === null ? null : (
        <div className="parent-toast" role="alert" key={toast.id}>
          <span>{t(toast.paused ? "alerts.toastPaused" : "alerts.toast")}</span>
          {ready && profileId !== undefined ? (
            <button
              type="button"
              className="parent-link"
              onClick={() => {
                setTab("alerts");
                setToast(null);
              }}
            >
              {t("tab.alerts")}
            </button>
          ) : null}
          <button
            type="button"
            className="parent-link"
            onClick={() => {
              setToast(null);
            }}
          >
            {t("alerts.dismiss")}
          </button>
        </div>
      )}
      <main className="parent-body">{content}</main>
    </div>
  );
}

/** The entry point on the child's screen, and parent mode on top of it while open. */
export function ParentEntry(props: { createSocket?: SocketFactory }) {
  const t = useParentT();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => {
    // Never leave the tablet signed in as a parent: end the email session and any PIN
    // unlock. Best effort: offline, both simply expire.
    void authApi.signOut();
    void api.endUnlock();
    setOpen(false);
  }, []);
  return open ? (
    <ParentMode
      onLeave={close}
      {...(props.createSocket ? { createSocket: props.createSocket } : {})}
    />
  ) : (
    <button
      type="button"
      className="parent-entry"
      aria-label={t("parent.openLabel")}
      onClick={() => {
        setOpen(true);
      }}
    >
      {t("parent.open")}
    </button>
  );
}
